import React, { Fragment, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Form, Col } from 'react-bootstrap';
import { AgGridColumn, AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import DatePicker, { registerLocale } from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Button from '../../../components/Button';
import EditButton from '../../../components/EditButton';
import ViewButton from '../../../components/ViewButton';
import ZipButton from '../../../components/GenerateZip';
import Loading from '../../../components/Loading';
import If from '../../../components/If';
import SearchEmpty from '../../../components/SearchEmpty';
import { STRINGS } from '../../../util/strings';
import { ExtensaoUsoListagemResults, StatusResults, CultureResults, RecordTypesResults } from '../../../models/models';
import ptBR from 'date-fns/locale/pt-BR';
import api, { apiFileDownload } from '../../../services/api';
import { _generateDate } from '../../../util/convert';
import { isAdmin } from '../../../users/isAdmin';
import { isRequesterWriteRegister, isRequesterReadRegister, isOnlyRequesterRegister, isTecnicoExtensaoUso, isOnlyTecnicoExtensaoUso } from '../../../users/isRequester';
import { DecodedToken } from '../../../models/user';
import jwtDecode from 'jwt-decode';
import { isReguladorRegister } from '../../../users/isRegulador';
import '../styles_list.css';
import { STATUS_EXTENSAO_USO } from '../../../util/statusExtensaoUso';
import {
	isUltimaExtensaoDoRegistroNaListagem,
	statusAposAprovacaoPeloRegulatorio,
} from '../../../util/extensaoUsoPermissoes';

registerLocale('pt-BR', ptBR);

export default function RegisterRNC ()
{
	const history = useHistory();
	const token = String(localStorage.getItem('token'));
	const { roles, unique_name }: DecodedToken = jwtDecode(token);
	const [registerRequests, setRegisterRequests] = useState<ExtensaoUsoListagemResults[]>([]);
	const [loadingRequests, setLoadingRequests] = useState<boolean>(true);
	const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
	const [emptySearch, setEmptySearch] = useState<boolean>(false);
	const [error, setError] = useState<boolean>(false);
	const [status, setStatus] = useState<StatusResults[]>([]);
	const [cultures, setCultures] = useState<CultureResults[]>([]);
	const [recordsType, setRecordsType] = useState<RecordTypesResults[]>([]);

	const { register, handleSubmit, control, setValue } = useForm({
		defaultValues: {
			code: '',
			requester: isOnlyRequesterRegister(roles) ? unique_name : '',
			initialDate: null,
			finalDate: null,
			status: '',
			culture: '',
		}
	});

	// Obtém o email do usuário logado para filtragem do técnico
	const getUserEmail = (): string => {
		try {
			const decoded: any = jwtDecode(token);
			// unique_name geralmente é o email do usuário
			const email = decoded.email?.toLowerCase() || decoded.unique_name?.toLowerCase() || '';
			console.log("Email do usuário logado:", email);
			return email;
		} catch {
			return '';
		}
	};

	// Filtra os registros para técnicos (apenas onde foi marcado como responsável)
	const filterForTecnico = (data: ExtensaoUsoListagemResults[]): ExtensaoUsoListagemResults[] => {
		const isTecnico = isOnlyTecnicoExtensaoUso(roles);
		
		if (!isTecnico) {
			return data;
		}
		
		const userEmail = getUserEmail();
		console.log("Filtrando para email:", userEmail);
		
		if (!userEmail) return [];

		const filtered = data.filter(item => {
			console.log("Item:", item.codigo, "Emails responsáveis:", item.emailsResponsaveis);
			return item.emailsResponsaveis?.some(email => 
				email?.toLowerCase() === userEmail
			);
		});
		
		console.log("Registros filtrados:", filtered.length);
		return filtered;
	};

	const getRequestsRegister = async () => {
		setError(false);
		try {
			// Usa o novo endpoint que lista registros aprovados pelo MAPA com status da ExtensaoUso
			const { data } = await api.get<ExtensaoUsoListagemResults[]>(
				`api/v1/cultivares/ExtensaoUso/listagem`
			);

			// Filtra para técnicos
			const filteredData = filterForTecnico(data);
			setRegisterRequests(filteredData);
		} catch (error:any) {
			setError(true);
		}
	}

	useEffect(() => {
		const getRequests = async () => {
			try {
			// Busca status específicos da ExtensaoUso
			const responseStatus = await api.get<StatusResults[]>('api/v1/cultivares/ExtensaoUso/status');
			const responseCulture = await api.get<CultureResults[]>('api/v1/cultivares/Cultura/obterporfiltro?Ativo=true');
			const responseRecordsType = await api.get<RecordTypesResults[]>('api/v1/cultivares/TipoRegistro/obterporfiltro?Ativo=true');
			
			setStatus(Array.isArray(responseStatus.data) ? responseStatus.data : []);
			setCultures(Array.isArray(responseCulture.data) ? responseCulture.data : []);
			setRecordsType(Array.isArray(responseRecordsType.data) ? responseRecordsType.data : []);
			} catch (error:any) {
				setError(true);
			}
			setLoadingRequests(false);
		}
		getRequests();
		getRequestsRegister();
	}, [])

	const search = async (queries?: any) => {
		setError(false);
		setLoadingSearch(true);

		let requesterUsername = queries?.requester;
		if (isOnlyRequesterRegister(roles)) {
			requesterUsername = unique_name;
		}

		try {
			// Query string via `params` do axios (encoding correto para &, espaços, acentos, etc.)
			const { data } = await api.get<ExtensaoUsoListagemResults[]>(
				`api/v1/cultivares/ExtensaoUso/listagem`,
				{
					params: {
						requisitante: requesterUsername?.trim() || undefined,
						codigo: queries?.code?.trim() || undefined,
						dataInicio: queries?.initialDate
							? _generateDate(new Date(queries.initialDate), 'En')
							: undefined,
						dataFim: queries?.finalDate
							? _generateDate(new Date(queries.finalDate), 'En')
							: undefined,
						culturaId: queries?.culture || undefined,
						denominacaoCultivarLinhagem:
							queries?.nameOfCultivarLineage?.trim() || undefined,
						tipoRegistroId: queries?.registerType || undefined,
					},
				}
			);

			// Filtra para técnicos
			const filteredData = filterForTecnico(data);

			if (filteredData.length) {
				setRegisterRequests(filteredData);
				setEmptySearch(false);
			} else {
				setEmptySearch(true);
			}

		} catch (error:any) {
			setError(true);
		}
		setLoadingSearch(false);
	}

	const getZip = async (id: number) => {
		try {
			const { data } = await apiFileDownload.get(`api/v1/cultivares/ExtensaoUso/downloadzip/${id}`);
			const blob = new Blob([data], { type: 'application/zip' });
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			if (typeof a.download === 'undefined') {
				window.location.href = url;
			} else {
				a.href = url;
				a.download = `extensao-uso-${id}`;
				document.body.appendChild(a);
				a.click();
			}
		} catch (error: any) {
			console.error(error);
		}
	};

	const podeEditarNaListagem = (row: ExtensaoUsoListagemResults) => {
		if (isAdmin(roles) || isReguladorRegister(roles)) return true;
		if (isRequesterReadRegister(roles) && !isRequesterWriteRegister(roles)) return false;
		if (isRequesterWriteRegister(roles) || isTecnicoExtensaoUso(roles)) {
			const ultima = isUltimaExtensaoDoRegistroNaListagem(row, registerRequests);
			const aposReg = statusAposAprovacaoPeloRegulatorio(row.statusExtensaoUso);
			const emRegulatorio = row.statusExtensaoUso === STATUS_EXTENSAO_USO.AguardandoRegulatorio;
			return ultima && !aposReg && !emRegulatorio;
		}
		return false;
	};

	const actions = ({ data }: any) => {
		const podeEditar = podeEditarNaListagem(data);

		let buttonEdit = podeEditar ? (
			<EditButton
				onClick={() => {
					history.push({
						pathname: '/cultivares/extensao-uso/editar',
						state: {
							id: data.registroId,
							codigo: data.codigo,
							codigoRegistro: data.codigoRegistro,
							novaExtensao: false
						}
					});
				}}
			/>
		) : null;

		let buttonView = !podeEditar ? (
			<ViewButton
				onClick={() => {
					history.push({
						pathname: '/cultivares/extensao-uso/editar',
						search: `?id=${data.registroId}&visualizar=1`,
						state: {
							id: data.registroId,
							codigo: data.codigo,
							codigoRegistro: data.codigoRegistro,
							novaExtensao: false
						}
					});
				}}
			/>
		) : null;

		let buttonZip = data.extensaoUsoId
			? <ZipButton Text="Gerar Zip" onClick={() => getZip(data.extensaoUsoId)} />
			: null;

		return <Fragment>
			{buttonEdit}
			{buttonView}
			&nbsp;&nbsp;
			{buttonZip}
		</Fragment>;
	}

	return <div className="container-custom-search">
		<div className="top-content">
			<h3>Consulta de Extensão de Uso de Registros RNC </h3>
		</div>
	
		<Form onSubmit={handleSubmit(search)}>
			<Form.Row>
				<Form.Group as={Col} md={3}>
					<Form.Label>Código</Form.Label>
					<Form.Control
						ref={register}
						name="code"
						maxLength={100}
						autoComplete="off"
					/>
				</Form.Group>
				<Form.Group as={Col} md={3}>
					<Form.Label>Denominação da Cultivar/Linhagem</Form.Label>
					<Form.Control
						ref={register}
						name="nameOfCultivarLineage"
						maxLength={100}
						autoComplete="off"
					/>
				</Form.Group>
				<Form.Group as={Col} md={3}>
					<Form.Label>Cultura</Form.Label>
					<Form.Control
						as="select"
						ref={register}
						name="culture"
					>
						<Form.Control
							as="option"
							label="Escolha uma cultura"
						/>
						{cultures.map(culture =>
							<Form.Control
								key={culture.id}
								as="option"
								label={culture.nome}
								value={culture.id}
							/>
						)}
					</Form.Control>
				</Form.Group>
				<Form.Group as={Col} md={3}>
					<Form.Label>Tipo de Registro</Form.Label>
					<Form.Control
						ref={register}
						as="select"
						name="registerType"
					>
						<Form.Control
							as="option"
							label="Escolha um tipo de registro"
						/>
						{recordsType.map(item =>
							<Form.Control
								as="option"
								key={item.id}
								label={item.nome}
								value={item.id}
							/>
						)}
					</Form.Control>
				</Form.Group>
				<Form.Group as={Col} md={3}>
					<Form.Label>Requisitante</Form.Label>
					<Form.Control
						ref={register}
						name="requester"
						maxLength={100}
						autoComplete="off"
						disabled={isOnlyRequesterRegister(roles)}
					/>
				</Form.Group>
				<Form.Group as={Col} md={2}>
					<Form.Label>
						Data inicial
					</Form.Label>
					<br />
					<Controller
						control={control}
						name="initialDate"
						render={({ onChange, value }) => (
							<DatePicker
								selected={value}
								onChange={onChange}
								className="input-custom-to-date"
								locale="pt-BR"
								dateFormat="dd/MM/yyyy"
								placeholderText="Selecionar a data"
							/>
						)}
					/>
				</Form.Group>
				<Form.Group as={Col} md={2}>
					<Form.Label>
						Data final
					</Form.Label>
					<br />
					<Controller
						control={control}
						name="finalDate"
						render={({ onChange, value }) => (
							<DatePicker
								selected={value}
								onChange={onChange}
								className="input-custom-to-date"
								locale="pt-BR"
								dateFormat="dd/MM/yyyy"
								placeholderText="Selecionar a data"
							/>
						)}
					/>
				</Form.Group>
				<Form.Group as={Col} md={3}>
					<Form.Label>Status</Form.Label>
					<Form.Control
						ref={register}
						as="select"
						name="status"
					>
						<Form.Control
							as="option"
							label="Escolha um status"
						/>
						{status.map(item =>
							<Form.Control
								as="option"
								label={item.nome}
								value={item.id}
							/>
						)}
					</Form.Control>
				</Form.Group>
				<span className="filter-btn">
					<Button
						title="Buscar"
						type="submit"
						contained
						isLoading={loadingSearch}
					/>
				</span>
			</Form.Row>
		</Form>
		<If condition={emptySearch}>
			<div className="div-empty">
				<SearchEmpty />
			</div>
		</If>
		<If condition={!loadingRequests && !error && !emptySearch && registerRequests.length}>
			<div className="search-content">
				<div className="ag-theme-alpine" style={{ height: "70vh", width: '100%' }}>
					<AgGridReact
						rowData={registerRequests}
						defaultColDef={{ flex: 1, sortable: true, resizable: true, sort: "" }}
						pagination
						paginationPageSize={10}
						frameworkComponents={{
							actions: actions,
						}}
					>
						<AgGridColumn
							headerName='Código'
							field='codigo'
							maxWidth={150}
							cellStyle={({ data }: any) => ({
								fontWeight: data.codigo !== data.codigoRegistro ? 'bold' : 'normal'
							})}
						/>
						<AgGridColumn
							headerName='Denominação da Cultivar/Linhagem'
							field='denominacaoDaCultivarLinhagem'
							cellStyle={{
								justifyContent: 'left'
							}}
						/>
						<AgGridColumn
							headerName='Cultura'
							field='nomeComumDaEspecie'
						/>
						<AgGridColumn
							headerName='Tipo de Registro'
							field='tipoRegistroNome'
						/>
						<AgGridColumn
							headerName='Requisitante'
							field='requisitanteExtensaoUso'
							cellStyle={{
								justifyContent: 'left'
							}}
						/>
						<AgGridColumn
							headerName='Data de criação'
							field='dataCriacao'
							valueFormatter={({ data }: any) => _generateDate(data.dataCriacao, 'Pt')}
						/>
					<AgGridColumn
						headerName='Status Extensão'
						field='statusExtensaoUsoDescricao'
					/>
					<AgGridColumn
						field='Ações'
						cellRenderer='actions'
						minWidth={180}
					/>
					</AgGridReact>
				</div>
			</div>
		</If>
		<If condition={loadingRequests}>
			<div className="loading-container">
				<Loading />
			</div>
		</If>
	</div>;
}